/**
 * The Overview band's four figures. Three — open work, to approve, equipment
 * out — are GET /dashboard/band's (api#1165), summed by the API over the
 * company's verticals; the fourth, certs, is GET /safety/team-cert-summary.
 * Nothing here counts rows: until api#1165 the band tallied /dispatch/jobs and
 * /equipment in the browser, and the API now counts the same row sets
 * (fieldiq-api src/lib/overview-ops/band.ts), so a casing-only company's band
 * is byte-identical (OverviewBand.casing-golden.test.tsx).
 *
 * Each figure is one of three states, never two:
 *
 *   loading → the read has not answered yet (the band pulses)
 *   failed  → the read failed or was refused, OR the API's total is null
 *             (no vertical could compute it, or the role is outside its
 *             gate): an em dash, never a pulse (fe#758) and never 0
 *   ready   → a total, and whether it is `partial` — some vertical did not
 *             compute, so the figure is a floor, not the whole count
 */
import { queryLoadFailure } from '../../lib/load-failure';
import { parseCount } from './buildActionAlerts';
import { useOverviewBand, useOverviewTeamCertSummary } from './overviewQueries';
import type { BandFigureRead, OverviewBandRead } from '../../types/overview-band';

export type Figure<T> =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; value: T };

export interface CountFigure {
  total: number;
  /** Some vertical did not compute (not computable or failed): the total is a floor. */
  partial: boolean;
}

export interface CertFigure {
  expired: number | null;
  expiring: number | null;
}

export interface BandFigures {
  open: Figure<CountFigure>;
  toApprove: Figure<CountFigure>;
  equipOut: Figure<CountFigure>;
  certs: Figure<CertFigure>;
}

function figure<T>(q: any, derive: (data: any) => T): Figure<T> {
  if (q.data !== undefined) return { status: 'ready', value: derive(q.data) };
  return queryLoadFailure(q) !== null ? { status: 'failed' } : { status: 'loading' };
}

/** One of the band's three figures. A null total is a dash, whatever the reason. */
function bandFigure(q: any, pick: (b: OverviewBandRead) => BandFigureRead): Figure<CountFigure> {
  // A null payload is not three zeros.
  if (q.data === null) return { status: 'failed' };
  if (q.data === undefined) return queryLoadFailure(q) !== null ? { status: 'failed' } : { status: 'loading' };
  const f = pick(q.data);
  if (!f || typeof f.total !== 'number') return { status: 'failed' };
  return { status: 'ready', value: { total: f.total, partial: f.status === 'partial' } };
}

export function useBandFigures(): BandFigures {
  const bandQ = useOverviewBand();
  const certQ = useOverviewTeamCertSummary();

  return {
    open: bandFigure(bandQ, (b) => b.figures?.open_work),
    toApprove: bandFigure(bandQ, (b) => b.figures?.to_approve),
    equipOut: bandFigure(bandQ, (b) => b.figures?.equipment_out),
    certs: figure(certQ, (cs: any) => ({
      expired: parseCount(cs?.expired),
      expiring: parseCount(cs?.expiringSoon),
    })),
  };
}
